import { JackpotTiers } from "ags/names";
import { Signal } from "signals";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import { FadeIn } from "slotworks/graphics/animation/base-animations";
import { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import { ButtonEvent, default as ButtonElement } from "slotworks/graphics/elements/button-element";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import soundManager from "slotworks/sound/sound-manager";
import Point from "slotworks/utils/geom/point";

export default class PickBonusComponent {
    public onCoinClicked: Signal = new Signal();

    protected coins: ButtonElement[] = [];
    private pickCount = 0;

    private revealedCoins: Map<number, Coin>;

    public start() {
        soundManager.customEvent("pick_music_level1");
        this.pickCount = 0;
        this.setup();
    }

    public revealCoin(coinIndex: number, tier: JackpotTiers) {
        const coin = new Coin(tier, coinIndex);
        this.revealedCoins.set(coinIndex, coin);
        coin.showHighlight();

        this.enableCoins();
    }

    public highlightCoins(coins: number[]) {
        if (coins.length === 3) {
            this.clearAllHighlights();
        }

        for (const coinIndex of coins) {
            this.highlightCoin(coinIndex);
        }
    }

    public end() {
        this.onCoinClicked.removeAll();
        this.disableCoins();
    }

    public hide() {
        this.clearAllHighlights();

        this.revealedCoins.forEach((coin: Coin) => {
            coin.destroy();
        });

        Layers.Bonus.defaultScene().execute();
        Layers.MatrixBackground.defaultScene().execute();

        CanvasManager.onOrientationChange.remove(this.switchOrientation);
    }

    protected switchOrientation = (orientation: Orientation) => {
        this.revealedCoins.forEach((coin: Coin) => {
            coin.refresh();
        });
    }

    private clearAllHighlights() {
        this.revealedCoins.forEach((coin: Coin) => {
            coin.clearHighlight();
        });
    }

    private highlightCoin(coin: number) {
        if (this.revealedCoins.get(coin)) {
            this.revealedCoins.get(coin).showHighlight();
        }
    }

    private setup() {
        Layers.Bonus.setScene("bonus").execute();
        if (Layers.MatrixBackground.hasScene("bonus") && !Layers.MatrixBackground.currentSceneIs("bonus")) {
            Layers.MatrixBackground.setScene("bonus").execute();
        }

        Coin.getCoinAnimation(JackpotTiers.GRAND).visible = false;
        Coin.getCoinAnimation(JackpotTiers.MINOR).visible = false;
        Coin.getCoinAnimation(JackpotTiers.MAJOR).visible = false;
        Coin.getCoinAnimation(JackpotTiers.MINI).visible = false;

        Coin.getRingAnimation(JackpotTiers.GRAND).visible = false;
        Coin.getRingAnimation(JackpotTiers.MINOR).visible = false;
        Coin.getRingAnimation(JackpotTiers.MAJOR).visible = false;
        Coin.getRingAnimation(JackpotTiers.MINI).visible = false;

        this.onCoinClicked.removeAll();
        this.coins = [];

        let index = 0;
        let coin;
        for (let i = 0; i < 12; i++) {
            index = i + 1;
            coin = Layers.Bonus.getButton("coin_" + index);
            coin.on(ButtonEvent.POINTER_UP, () => {
                this.coinClicked(i);
            });

            this.coins.push(coin);
        }

        this.revealedCoins = new Map<number, Coin>();

        CanvasManager.onOrientationChange.add(this.switchOrientation);

        this.switchOrientation(CanvasManager.orientation);

    }

    private coinClicked(index: number) {
        this.pickCount++;

        this.disableCoins();

        this.onCoinClicked.dispatch(index + 1, this.pickCount);
    }

    private disableCoins() {
        this.coins.forEach((coin: ButtonElement) => {
            coin.setEnabled(false);
        });
    }

    private enableCoins() {
        this.coins.forEach((coin: ButtonElement) => {
            coin.setEnabled(true);
        });
    }
}

export class Coin {
    public static getCoinAnimation(tier: JackpotTiers): AnimatedSprite {
        return Layers.Bonus.getAnimatedSprite(tier + "_baby_coin");
    }

    public static getRingAnimation(tier: JackpotTiers): AnimatedSprite {
        return Layers.Bonus.getAnimatedSprite("ring_" + tier);
    }

    private button: ButtonElement;
    private animation: AnimatedSprite;
    private highlight: AnimatedSprite;

    constructor(public tier: JackpotTiers, public index: number) {
        this.button = Layers.Bonus.getButton("coin_" + this.index);
        this.animation = GraphicsManager.createAnimation(this.tier + "_baby_coin");
        this.highlight = GraphicsManager.createAnimation("ring_" + this.tier);
        this.highlight.visible = false;
        this.highlight.alpha = 0;

        Layers.Bonus.add(this.highlight);
        Layers.Bonus.add(this.animation);

        this.move();
    }

    public refresh() {
        this.move();
        if (this.highlight.visible) {
            this.showHighlight();
        }
    }

    public move() {
        const coinOne = Layers.Bonus.getButton("coin_1");
        const position = Coin.getCoinAnimation(this.tier);

        const relativePosition = new Point(coinOne.x - position.x, coinOne.y - position.y);

        this.animation.x = this.button.x - relativePosition.x;
        this.animation.y = this.button.y - relativePosition.y;

        this.animation.width = position.width;
        this.animation.height = position.height;

        this.button.setVisible(false);
        this.animation.play();
    }

    public showHighlight() {
        const coinOne = Layers.Bonus.getButton("coin_1");
        const position = Coin.getRingAnimation(this.tier);

        const relativePosition = new Point(coinOne.x - position.x, coinOne.y - position.y);

        this.highlight.x = this.button.x - relativePosition.x;
        this.highlight.y = this.button.y - relativePosition.y;

        this.highlight.width = position.width;
        this.highlight.height = position.height;

        this.highlight.loop = true;
        this.highlight.visible = true;
        this.highlight.play();

        if (this.highlight.alpha === 0) { FadeIn(this.highlight, 500, 300).execute(); }
    }

    public clearHighlight() {
        this.highlight.gotoAndStop(0);
        this.highlight.visible = false;
    }

    public destroy() {
        this.button.destroy();
        this.animation.destroy();
        if (this.highlight) {
            this.highlight.destroy();
        }

        this.button = null;
        this.animation = null;
        this.highlight = null;
    }
}
